import type { ReactNode } from "react";
import { ChatCircleText, Files, GridFour, IdentificationCard, Key, ListChecks } from "@phosphor-icons/react";
import "./auth.css";

/**
 * The frame every page outside the shell stands in, after `authFrame` in
 * `prototype/kund/pages-auth.js` (approved 2026-09-24): the small wordmark, then one box on the
 * canvas. On the sign-in page `brand` adds the display heading, which on a computer becomes the dark
 * half beside the box, with the six lines and the picture of Översikt under it (build round 7,
 * 2026-09-28, from the sketch approved that day).
 *
 * One tree for both widths rather than a copy of the wordmark and heading per width. The sketch drew
 * them twice and hid one with CSS; here the header is the brand half on a computer and the lines
 * above the box on a phone, so a screen reader and a test meet the heading once.
 *
 * The box is a `panel` with no head row: the page's title is the first thing in its body, because a
 * page outside the shell has nothing else to put in a head row.
 */
export function AuthFrame({
  brand = false,
  titleId,
  role,
  testId,
  children
}: {
  brand?: boolean;
  /** The id of the h2 inside `children`, which names the box. */
  titleId?: string;
  role?: "alert";
  testId?: string;
  children: ReactNode;
}) {
  return (
    <div className={`k-auth${brand ? " has-brand" : ""}`}>
      <header className="k-auth-brand">
        <p className="k-wordmark">
          <b>PETROVIC SYSTEMS</b>
          <span>Kundportal</span>
        </p>
        {brand ? (
          <>
            <h1 className="k-display">Välkommen till er kundportal.</h1>
            <PortalFeatures />
            <PortalScene />
          </>
        ) : null}
      </header>
      <main className="k-auth-main">
        <section
          className="panel k-auth-card"
          aria-labelledby={titleId}
          role={role}
          data-testid={testId}
        >
          <div className="panel-body">{children}</div>
        </section>
      </main>
    </div>
  );
}


/*
 * What the portal is for, one line each with the icon the same place carries inside it (build round
 * 7, 2026-09-28, Mattej's six lines). A real list, so a screen reader hears "list, six items"; the
 * icons are decoration beside the words. Shown on a computer only, where the brand half has the room;
 * the phone gets the heading and the box.
 */
const FEATURES = [
  { Icon: GridFour, text: "Se hur webbplatsen mår och presterar" },
  { Icon: ChatCircleText, text: "Chatta med Petrovic Systems" },
  { Icon: ListChecks, text: "Följ era ärenden" },
  { Icon: Files, text: "Hämta avtal och dokument" },
  { Icon: IdentificationCard, text: "Se vem som äger era konton" },
  { Icon: Key, text: "Skicka lösenord säkert" }
] as const;

function PortalFeatures() {
  return (
    <ul className="k-features" data-testid="login-features">
      {FEATURES.map(({ Icon, text }) => (
        <li key={text}>
          <Icon size={20} aria-hidden="true" />
          {text}
        </li>
      ))}
    </ul>
  );
}

/*
 * The portal, drawn rather than described (sign-in sketch, 2026-09-27). A quiet picture of Översikt:
 * the month's visits as bars, a service that is up, and a message in the chat. Shapes only, no real
 * figure and no name, so nothing here can be read as a client's data, and all of it is hidden from a
 * screen reader because it says nothing the heading does not. Shown on a computer only.
 */
const VISITS = [38, 52, 44, 61, 57, 70, 48, 66, 74, 63, 81, 72];

function PortalScene() {
  return (
    <div className="k-scene" aria-hidden="true">
      <div className="k-scene-window">
        <div className="k-scene-top">
          <span className="k-scene-line is-short" />
          <span className="k-scene-avatar" />
        </div>
        <div className="k-scene-grid">
          <div className="k-scene-card is-visits">
            <span className="k-scene-line is-label" />
            <svg className="k-scene-bars" viewBox="0 0 240 90" preserveAspectRatio="none">
              {VISITS.map((height, index) => (
                <rect
                  key={index}
                  x={index * 20 + 4}
                  y={90 - height}
                  width="12"
                  height={height}
                  rx="3"
                  className={index === VISITS.length - 1 ? "is-now" : undefined}
                />
              ))}
            </svg>
          </div>
          <div className="k-scene-card is-status">
            <span className="k-scene-dot" />
            <b>Uppe</b>
            <span className="k-scene-line is-fill" />
            <span className="k-scene-ticks">
              {Array.from({ length: 30 }, (_, index) => (
                <i key={index} />
              ))}
            </span>
          </div>
          <div className="k-scene-card is-chat">
            <span className="k-scene-bubble is-theirs">
              <span className="k-scene-line is-fill" />
              <span className="k-scene-line is-half" />
            </span>
            <span className="k-scene-bubble is-mine">
              <span className="k-scene-line is-fill" />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
